// ============================================================
// BLINKBLIS — CENTRO DE CONTROL DE OFERTAS
// ============================================================
// Este es el UNICO archivo que tocas para cambiar links de afiliado
// o el contenido de una oferta.
//
// TODO el contenido es bilingue: cada campo lleva { es, en }.
// Si agregas una oferta nueva, copia el bloque completo y traduce
// ambos idiomas — si dejas uno vacio, el sitio se ve a medias.
// ============================================================

import { attributionTag } from './attribution'

export type Lang = 'es' | 'en'
export type L<T> = Record<Lang, T>

export const pick = <T,>(value: L<T>, lang: Lang): T =>
  (value && value[lang]) ?? value.es

export type Offer = {
  slug: string
  brand: string
  affiliateUrl: string // link crudo de impact, SIN subId (se agrega solo)
  network: string
  confidence: number
  updated: L<string>
  readMinutes: number
  category: L<string>
  title: L<string>
  subtitle: L<string>
  ctaLabel: L<string>
  facts: L<{ label: string; value: string }[]>
  pros: L<string[]>
  cons: L<string[]>
  notFor: L<string[]>
  faq: L<{ q: string; a: string }[]>
}

// ============================================================
// SUB ID
// ------------------------------------------------------------
// NO metas subIds en el formulario "Advanced" de impact: quedan
// fijos para siempre y no te dicen que anuncio convirtio.
//
// En Meta Ads:
//   Website URL      -> https://blinkblis.com/oferta/credito
//   URL parameters   -> utm_source={{site_source_name}}
//                       &utm_medium=paid_social
//                       &utm_campaign={{campaign.name}}
//                       &utm_content={{ad.name}}
//                       &campaign_id={{campaign.id}}
//                       &adset_id={{adset.id}}
//                       &ad_id={{ad.id}}
//
// attributionTag() lee esos parametros y los recuerda durante toda
// la sesion, asi que el subId sigue siendo correcto aunque el usuario
// navegue por el sitio y pierda el query string de la URL.
// ============================================================
export const withSubId = (url: string, slug: string) => {
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}subId1=${encodeURIComponent(attributionTag(slug))}`
}

export const OFFERS: Offer[] = [
  {
    slug: 'credito',
    brand: 'Kovo',
    affiliateUrl: 'https://kovo-credit.sjv.io/enZ41Q',
    network: 'impact.com',
    confidence: 88,
    readMinutes: 7,
    updated: { es: 'Septiembre 2026', en: 'September 2026' },
    category: { es: 'Credito', en: 'Credit' },
    title: {
      es: 'Como construir historial crediticio desde cero en Estados Unidos',
      en: 'How to build credit from scratch in the United States',
    },
    subtitle: {
      es: 'Sin tarjeta, sin historial y sin que nadie te apruebe nada. Que funciona de verdad, que es humo, y cual es la opcion que recomendamos.',
      en: 'No card, no history, no approval needed. What actually works, what is noise, and the option we recommend.',
    },
    ctaLabel: {
      es: 'Abrir mi cuenta en Kovo',
      en: 'Open my Kovo account',
    },
    facts: {
      // VERIFICAR en kovocredit.com antes de publicar datos de producto.
      // Lo que dejes vacio simplemente no se muestra.
      es: [],
      en: [],
    },
    pros: {
      es: [
        'Entrada sin historial crediticio previo',
        'Pagos automaticos, no dependes de acordarte cada mes',
        'Apertura en linea, en minutos',
      ],
      en: [
        'No prior credit history required to open',
        'Automatic payments, so you do not have to remember each month',
        'Opens online, in minutes',
      ],
    },
    cons: {
      es: [
        'Es un compromiso de meses: la cuenta tiene que mantenerse abierta y al dia para que sirva',
        'No repara credito danado ni elimina cuentas en colecciones',
        'Es un producto de un solo proposito, no una tarjeta de uso general',
      ],
      en: [
        'It is a multi-month commitment: the account has to stay open and current to be worth anything',
        'It does not repair damaged credit or remove collections',
        'It is a single-purpose product, not a general-use card',
      ],
    },
    notFor: {
      es: [
        'Si ya tienes historial solido: tu siguiente paso es otro producto',
        'Si tienes deudas en colecciones activas: resolver eso primero pesa mas',
        'Si no puedes sostener el pago mensual: un atraso aqui te perjudica igual que en cualquier cuenta',
        'Si esperas resultados en semanas: ningun camino real lo hace',
      ],
      en: [
        'If you already have solid credit history: your next step is a different product',
        'If you have active collections: resolving those first matters more',
        'If you cannot sustain the monthly payment: a late payment here hurts you like any other account',
        'If you expect results in weeks: no real path does that',
      ],
    },
    faq: {
      es: [
        {
          q: 'Cuanto tarda en verse reflejado?',
          a: 'Los buros actualizan en ciclos tipicamente mensuales, y el historial se construye con el tiempo acumulado de pagos reportados. No existe un plazo garantizado y varia segun tu situacion.',
        },
        {
          q: 'Esto borra lo negativo que ya tengo?',
          a: 'No. Ningun producto de credit builder elimina informacion negativa exacta y verificable de tu reporte. Construye historial nuevo; no borra el viejo.',
        },
        {
          q: 'Me garantiza que me aprueben una tarjeta despues?',
          a: 'No. Ningun producto puede garantizar la aprobacion de otro. Lo que hace es generar historial de pagos reportado, que es uno de los factores que los emisores consideran.',
        },
        {
          q: 'Que pasa si cancelo a los dos meses?',
          a: 'Pierdes el beneficio principal. El historial se construye con pagos sostenidos en el tiempo, asi que una cuenta abierta y cerrada rapido aporta muy poco. Si no vas a sostenerlo varios meses, no vale la pena abrirlo.',
        },
        {
          q: 'Por que Blinkblis recomienda esto?',
          a: 'Porque para alguien que empieza desde cero cumple los cuatro criterios que evaluamos: entrada sin historial, costo bajo, reporte a buros y terminos claros. Ganamos una comision si abres una cuenta, y eso esta declarado en cada pagina.',
        },
      ],
      en: [
        {
          q: 'How long until it shows up?',
          a: 'Bureaus typically update on monthly cycles, and history builds from accumulated reported payments over time. There is no guaranteed timeline and it varies by situation.',
        },
        {
          q: 'Does this erase the negative marks I already have?',
          a: 'No. No credit builder product removes accurate, verifiable negative information from your report. It builds new history; it does not erase the old.',
        },
        {
          q: 'Does it guarantee I get approved for a card later?',
          a: 'No. No product can guarantee approval for another. What it does is generate reported payment history, which is one of the factors issuers consider.',
        },
        {
          q: 'What if I cancel after two months?',
          a: 'You lose the main benefit. History is built through sustained payments over time, so an account opened and closed quickly contributes very little. If you are not going to keep it for several months, it is not worth opening.',
        },
        {
          q: 'Why does Blinkblis recommend this?',
          a: 'Because for someone starting from zero it meets the four criteria we evaluate: no history required, low cost, bureau reporting and clear terms. We earn a commission if you open an account, and that is disclosed on every page.',
        },
      ],
    },
  },
]

export const getOffer = (slug?: string) => OFFERS.find((o) => o.slug === slug)
